// ============================================
// APPLICATION - Producto Hooks
// ============================================

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productoRepository } from '../../infrastructure/http/producto.repository';
import { queryKeys } from '../query-keys';
import { useCallback } from 'react';
import type {
  CreateProductoDto,
  UpdateProductoDto,
  ProductoFiltros,
} from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';

/**
 * Hook para listar productos con paginación
 */
export const useProductos = (filtros?: ProductoFiltros) => {
  return useQuery({
    queryKey: queryKeys.productos.list(filtros),
    queryFn: () => productoRepository.list(filtros),
    staleTime: 30 * 1000, // 30 segundos
  });
};

/**
 * Hook para obtener un producto por ID
 */
export const useProducto = (id: string) => {
  return useQuery({
    queryKey: queryKeys.productos.detail(id),
    queryFn: () => productoRepository.getById(id),
    enabled: Boolean(id),
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Hook para búsqueda de productos (autocompletado)
 */
export const useProductoSearch = (query: string) => {
  return useQuery({
    queryKey: queryKeys.productos.search(query),
    queryFn: () => productoRepository.search(query),
    enabled: query.length >= 2,
    staleTime: 10 * 1000,
  });
};

/**
 * Hook para escanear código de barras
 */
export const useScanBarcode = (scanBarCode: string) => {
  return useQuery({
    queryKey: ['productos', 'scan', scanBarCode],
    queryFn: () => productoRepository.scanBarcode(scanBarCode),
    enabled: scanBarCode.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: false, // No reintentar si no encuentra el producto
  });
};

/**
 * Hook para buscar imágenes de productos
 */
export const useBuscarImagenes = (nombre: string) => {
  return useQuery({
    queryKey: ['imagenes-productos', nombre],
    queryFn: () => productoRepository.buscarImagenes(nombre),
    enabled: nombre.length >= 3,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};

/**
 * Hook para obtener productos vendibles (inventario)
 */
export const useProductosVendibles = (
  page: number = 0,
  size: number = 20,
  searchTerm?: string,
  sort?: string,
  categoriaAlmacenId?: number,
  alerta?: string,
  soloPacks?: boolean
) => {
  return useQuery({
    queryKey: ['productos', 'sellable', page, size, searchTerm, sort, categoriaAlmacenId, alerta, soloPacks],
    queryFn: () => productoRepository.getSellableProducts(page, size, searchTerm, sort, categoriaAlmacenId, alerta, soloPacks),
    staleTime: 30 * 1000, // 30 segundos
    // Al cambiar orden/página/búsqueda se sigue mostrando la tabla anterior en vez de dejarla en blanco
    placeholderData: keepPreviousData,
  });
};


/**
 * Hook para obtener precios promedio de un producto
 */
export const usePreciosPromedio = (productoId: number | null, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['productos', 'precios-promedio', productoId],
    queryFn: () => productoRepository.obtenerPreciosPromedio(productoId!),
    enabled: enabled && productoId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });
};

export const useProductosVendiblesBarcode = () => {
  const queryClient = useQueryClient();

  return useCallback(async (barcode: string, size: number = 20, sort?: string) => {
    if (barcode.length < 2) return null;

    const page = 0;
    return queryClient.fetchQuery({
      queryKey: ['productos', 'sellable', 'search', 'barcode', page, size, barcode, sort],
      queryFn: () => productoRepository.searchSellableProducts(page, size, barcode, sort),
    });
  }, [queryClient]);
};

/**
 * Buscador/escáner de productos del POS con Anterior/Siguiente (sin COUNT(*) en el
 * backend). Usar solo para el listado de venta (VentasPage) — no soporta filtro por
 * categoría; para listados con paginación numérica completa (Inventario, selectores de
 * pack) usar useProductosVendibles.
 *
 * A diferencia de la paginación clásica, no hay total de páginas: cada página trae su
 * propio `hasNext` para habilitar/deshabilitar "Siguiente". Cada página se cachea por
 * separado (por `page` en la queryKey), así que volver "Atrás" no vuelve a pegarle al
 * backend si esa página ya se cargó antes.
 */
export const useSearchProductosVendibles = (
  page: number = 0,
  searchTerm?: string,
  size: number = 20,
  sort?: string,
  enabled: boolean = true,
  excluirPacks: boolean = false
) => {
  return useQuery({
    queryKey: ['productos', 'sellable', 'search', page, size, searchTerm, sort, excluirPacks],
    queryFn: () => productoRepository.searchSellableProducts(page, size, searchTerm, sort, excluirPacks),
    staleTime: 30 * 1000, // 30 segundos
    enabled,
  });
};

/**
 * Pestaña "Packs" del POS (VentasPage): mismo contrato que useSearchProductosVendibles pero
 * contra la vista de packs. `enabled` evita pedir datos mientras la pestaña no está activa.
 * Comparte el prefijo ['productos', 'sellable'], así que las invalidaciones de crear/editar
 * pack y del checkout también la alcanzan.
 */
export const useSearchPacksVendibles = (
  page: number = 0,
  searchTerm?: string,
  size: number = 20,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['productos', 'sellable', 'packs', page, size, searchTerm],
    queryFn: () => productoRepository.searchSellablePacks(page, size, searchTerm),
    // Los packs cambian poco y la lista no muestra stock: se invalidan al crear/editar/eliminar
    // un pack, así que solo otro dispositivo puede dejarla desactualizada hasta este plazo.
    staleTime: 5 * 60 * 1000, // 5 minutos
    enabled,
  });
};

/** Escáner del POS con la pestaña Packs activa: busca el código solo entre packs. */
export const usePacksVendiblesBarcode = () => {
  const queryClient = useQueryClient();

  return useCallback(async (barcode: string, size: number = 20) => {
    if (barcode.length < 2) return null;

    const page = 0;
    return queryClient.fetchQuery({
      queryKey: ['productos', 'sellable', 'packs', 'barcode', page, size, barcode],
      queryFn: () => productoRepository.searchSellablePacks(page, size, barcode, undefined, 'barcode'),
    });
  }, [queryClient]);
};

/**
 * Total del catálogo activo del almacén (sin filtro de búsqueda), para mostrar
 * "N cargados de TOTAL existentes" junto al buscador del POS. Se pide una sola vez por
 * sesión (staleTime largo) — es un COUNT liviano sobre la tabla base, no sobre la vista
 * agregada, así que no reintroduce el costo que se sacó de search().
 */
export const useCountProductosVendibles = () => {
  return useQuery({
    queryKey: ['productos', 'sellable', 'count'],
    queryFn: () => productoRepository.countSellableProducts(),
    staleTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook para obtener TODOS los productos vendibles (sin paginación) - útil para exportar
 */
export const useAllProductosVendibles = (searchTerm?: string) => {
  return useQuery({
    queryKey: ['productos', 'sellable', 'all', searchTerm],
    queryFn: () => productoRepository.getAllSellableProducts(searchTerm),
    enabled: false, // Solo se ejecuta manualmente con refetch()
  });
};

/**
 * Hook para crear producto
 */
export const useCreateProducto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateProductoDto) => productoRepository.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.lists() });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para actualizar producto
 */
export const useUpdateProducto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateProductoDto }) =>
      productoRepository.update(id, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.lists() });
      queryClient.setQueryData(queryKeys.productos.detail(data.id), data);
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para eliminar producto
 */
export const useDeleteProducto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productoRepository.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.lists() });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para eliminar producto de almacén (inventario)
 */
export const useDeleteProductoAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productoAlmacenId: number) => productoRepository.deleteProductoAlmacen(productoAlmacenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para obtener productos destacados (top vendidos y stock bajo)
 */
export const useProductosDestacados = (enabled = false) => {
  return useQuery({
    queryKey: ['productos', 'destacados'],
    queryFn: () => productoRepository.getTopSellingProducts(),
    staleTime: 60 * 1000, // 1 minuto
    enabled, // Solo se ejecuta cuando enabled es true
  });
};

export const useProductoVendibleById = (productoAlmacenId: number) => {
  return useQuery({
    queryKey: ['producto', 'vendible', productoAlmacenId],
    queryFn: () => productoRepository.getProductoVendibleById(productoAlmacenId),
    enabled: !!productoAlmacenId,
    retry: false,
  });
};

export const useInventarioProducto = (productoAlmacenId: number, enabled = false) => {
  return useQuery({
    queryKey: ['inventario', 'producto', productoAlmacenId],
    queryFn: () => productoRepository.getInventarioProducto(productoAlmacenId),
    enabled: enabled && !!productoAlmacenId,
  });
};

export const useAddInventario = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dto: any) => productoRepository.addInventario(dto),
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['inventario', 'producto', data.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', data.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};

export const useUpdateInventario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inventarioId, dto }: { inventarioId: number; dto: any }) =>
      productoRepository.updateInventario(inventarioId, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['inventario', 'producto', data.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', data.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
      queryClient.invalidateQueries({ queryKey: ['reportes', 'alertas-inventario'] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

export const useDeleteInventario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inventarioId }: { inventarioId: number; productoAlmacenId: number }) =>
      productoRepository.deleteInventario(inventarioId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventario', 'producto', variables.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', variables.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
      queryClient.invalidateQueries({ queryKey: ['reportes', 'alertas-inventario'] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

export const useMermaProducto = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dto: any) => productoRepository.mermaProducto(dto),
    onSuccess: (data) => {
      // Invalidar y actualizar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['inventario', 'producto', data.productoAlmacenId] });
      queryClient.setQueryData(['producto', 'vendible', data.productoAlmacenId], data);
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};

export const useUpdateProductoAlmacen = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productoAlmacenId, dto }: { productoAlmacenId: number; dto: any }) => 
      productoRepository.updateProductoAlmacen(productoAlmacenId, dto),
    onSuccess: (data) => {
      // Actualizar producto en cach\u00e9
      queryClient.setQueryData(['producto', 'vendible', data.productoAlmacenId], data);
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};
/**
 * Hook para agregar producto al almacén
 */
export const useAgregarProductoAlmacen = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dto: {
      productoId: number | null;
      productoCodigo: string;
      productoNombre: string;
      imagenProductoId?: number;
      precioCompraActual: number;
      precioVentaActual: number;
      venderPor: 'U' | 'F';
      stock: number;
      fechaVencimiento: string | null;
      esPack: boolean;
    }) => productoRepository.agregarProductoAlmacen(dto),
    onSuccess: () => {
      // Invalidar lista de productos vendibles
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};
/**
 * Hook para obtener detalles de un pack
 */
export const useObtenerPack = (productoAlmacenId: number) => {
  return useQuery({
    queryKey: ['pack', productoAlmacenId],
    queryFn: () => productoRepository.obtenerPack(productoAlmacenId),
    enabled: !!productoAlmacenId,
  });
};

/**
 * Hook para actualizar un pack existente
 */
export const useActualizarPack = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dto: {
      productoAlmacenId: number;
      nombre: string;
      precio: number;
      imagenId?: number | null;
      items: Array<{
        productoHijoId: number;
        cantidad: number;
      }>;
    }) => productoRepository.actualizarPack(dto),
    onSuccess: () => {
      // Invalidar lista de productos vendibles y el pack específico
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
      queryClient.invalidateQueries({ queryKey: ['pack'] });
    },
  });
};
/**
 * Hook para crear pack de productos
 */
export const useCrearPack = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dto: {
      nombre: string;
      codigo?: string;
      precio: number;
      imagen?: string | null;
      items: Array<{
        productoHijoId: number;
        cantidad: number;
      }>;
    }) => productoRepository.crearPack(dto),
    onSuccess: () => {
      // Invalidar lista de productos vendibles
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};

/**
 * Hook para establecer alias de nombre de un producto en el almacén
 */
export const useSetNombreProductoAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productoAlmacenId, nombre }: { productoAlmacenId: number; nombre: string }) =>
      productoRepository.setNombreProductoAlmacen(productoAlmacenId, nombre),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', variables.productoAlmacenId] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para eliminar alias de nombre de un producto en el almacén
 */
export const useDeleteNombreProductoAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productoAlmacenId: number) =>
      productoRepository.deleteNombreProductoAlmacen(productoAlmacenId),
    onSuccess: (_, productoAlmacenId) => {
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', productoAlmacenId] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para obtener inventario valorizado con paginación
 */
export const useInventarioValorizado = (page: number = 0, size: number = 20, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['inventario-valorizado', page, size],
    queryFn: () => productoRepository.getInventarioValorizado(page, size),
    enabled,
    staleTime: 30 * 1000, // 30 segundos
  });
};